import { offlineStorage } from './indexedDBOfflineService';

// Network connectivity and offline authentication service
export interface NetworkStatus {
  isOnline: boolean;
  isOffline: boolean;
  connectionType: 'online' | 'offline' | 'slow';
}

export interface OfflineAuthState {
  isAuthenticated: boolean;
  hasValidToken: boolean;
  tokenExpiration: Date | null;
  canWorkOffline: boolean;
  username: string | null;
}

export class OfflineAuthService {
  private static instance: OfflineAuthService;
  private networkStatus: NetworkStatus = {
    isOnline: navigator.onLine,
    isOffline: !navigator.onLine,
    connectionType: navigator.onLine ? 'online' : 'offline',
  };
  private listeners: Array<(status: NetworkStatus) => void> = [];
  private authListeners: Array<(authState: OfflineAuthState) => void> = [];

  private constructor() {
    this.initializeNetworkListeners();
  }

  public static getInstance(): OfflineAuthService {
    if (!OfflineAuthService.instance) {
      OfflineAuthService.instance = new OfflineAuthService();
    }
    return OfflineAuthService.instance;
  }

  private initializeNetworkListeners() {
    window.addEventListener('online', () => {
      this.updateNetworkStatus(true);
    });

    window.addEventListener('offline', () => {
      this.updateNetworkStatus(false);
    });

    // Additional connectivity check for more reliable detection
    setInterval(() => this.performConnectivityCheck(), 30000); // Check every 30 seconds
  }

  private async performConnectivityCheck() {
    if (!navigator.onLine) {
      this.updateNetworkStatus(false);
      return;
    }

    try {
      // Try to fetch a small resource to verify actual connectivity
      const response = await fetch('/favicon.ico', {
        method: 'HEAD',
        cache: 'no-store',
        signal: AbortSignal.timeout(5000), // 5 second timeout
      });

      if (response.ok) {
        this.updateNetworkStatus(true);
      } else {
        this.updateNetworkStatus(false, 'slow');
      }
    } catch {
      this.updateNetworkStatus(false);
    }
  }

  private updateNetworkStatus(isOnline: boolean, connectionType?: 'slow') {
    const newStatus: NetworkStatus = {
      isOnline,
      isOffline: !isOnline,
      connectionType: connectionType || (isOnline ? 'online' : 'offline'),
    };

    if (JSON.stringify(newStatus) !== JSON.stringify(this.networkStatus)) {
      this.networkStatus = newStatus;
      this.notifyListeners();
      this.checkAuthState(); // Re-evaluate auth state when network changes
    }
  }

  public getNetworkStatus(): NetworkStatus {
    return { ...this.networkStatus };
  }

  public addNetworkListener(
    listener: (status: NetworkStatus) => void
  ): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  public addAuthListener(
    listener: (authState: OfflineAuthState) => void
  ): () => void {
    this.authListeners.push(listener);
    return () => {
      const index = this.authListeners.indexOf(listener);
      if (index > -1) {
        this.authListeners.splice(index, 1);
      }
    };
  }

  private notifyListeners() {
    this.listeners.forEach((listener) => {
      try {
        listener(this.networkStatus);
      } catch (error) {
        console.warn('Error in network status listener:', error);
      }
    });
  }

  private notifyAuthListeners(authState: OfflineAuthState) {
    this.authListeners.forEach((listener) => {
      try {
        listener(authState);
      } catch (error) {
        console.warn('Error in auth state listener:', error);
      }
    });
  }

  public async checkAuthState(): Promise<OfflineAuthState> {
    const account = await this.getStoredAccount();
    const accessToken = await this.getStoredAccessToken();

    if (!account || !accessToken) {
      const authState: OfflineAuthState = {
        isAuthenticated: false,
        hasValidToken: false,
        tokenExpiration: null,
        canWorkOffline: false,
        username: null,
      };
      this.notifyAuthListeners(authState);
      return authState;
    }

    const tokenInfo = this.decodeToken(accessToken);
    const isTokenValid = tokenInfo && this.isTokenValid(tokenInfo);
    const canWorkOffline =
      isTokenValid && this.isTokenSuitableForOffline(tokenInfo);

    const authState: OfflineAuthState = {
      isAuthenticated: true,
      hasValidToken: isTokenValid,
      tokenExpiration: tokenInfo?.exp ? new Date(tokenInfo.exp * 1000) : null,
      canWorkOffline,
      username: account.username || null,
    };

    this.notifyAuthListeners(authState);
    return authState;
  }

  private async getStoredAccount() {
    try {
      const account = await offlineStorage.getAuthData('auth.account');
      return account;
    } catch {
      // Fallback to localStorage for backward compatibility
      try {
        const localAccount = localStorage.getItem('auth.account');
        return localAccount ? JSON.parse(localAccount) : null;
      } catch {
        return null;
      }
    }
  }

  private async getStoredAccessToken(): Promise<string | null> {
    try {
      const token = await offlineStorage.getAuthData('auth.accessToken');
      return token;
    } catch {
      // Fallback to localStorage for backward compatibility
      return localStorage.getItem('auth.accessToken');
    }
  }

  private decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      if (!base64Url) return null;

      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }

  private isTokenValid(tokenInfo: any): boolean {
    if (!tokenInfo || !tokenInfo.exp) return false;

    const now = Math.floor(Date.now() / 1000);
    const expirationTime = tokenInfo.exp;

    // Token is valid if it hasn't expired (with 5 minute buffer)
    return expirationTime > now + 300;
  }

  private isTokenSuitableForOffline(tokenInfo: any): boolean {
    if (!this.isTokenValid(tokenInfo)) return false;

    const now = Math.floor(Date.now() / 1000);
    const expirationTime = tokenInfo.exp;

    // Import token config - using hardcoded value for now to avoid dynamic import complexity
    const OFFLINE_CAPABILITY_BUFFER = 1 * 60 * 60; // 1 hour = 3600 seconds (realistic for AAD)

    // Token is suitable for offline if it has enough time remaining for inspection work
    return expirationTime > now + OFFLINE_CAPABILITY_BUFFER;
  }

  public async handleOfflineMode(): Promise<OfflineAuthState> {
    console.log('Application entering offline mode');

    // Store the fact that we're offline
    await offlineStorage.setAuthData('auth.isOffline', 'true');
    await offlineStorage.setAuthData(
      'auth.offlineTimestamp',
      Date.now().toString()
    );

    return this.checkAuthState();
  }

  public async handleOnlineMode(): Promise<void> {
    console.log('Application coming back online');

    // Remove offline markers
    await offlineStorage.removeAuthData('auth.isOffline');
    await offlineStorage.removeAuthData('auth.offlineTimestamp');

    // Trigger a re-evaluation of auth state
    await this.checkAuthState();

    // Dispatch event to notify components that we're back online
    window.dispatchEvent(new CustomEvent('auth-online-restored'));
  }

  public isCurrentlyOffline(): boolean {
    return this.networkStatus.isOffline;
  }

  public async getOfflineDuration(): Promise<number> {
    const offlineTimestamp = await offlineStorage.getAuthData(
      'auth.offlineTimestamp'
    );
    if (!offlineTimestamp) {
      // Fallback to localStorage for backward compatibility
      const localTimestamp = localStorage.getItem('auth.offlineTimestamp');
      if (!localTimestamp) return 0;
      return Date.now() - parseInt(localTimestamp, 10);
    }

    return Date.now() - parseInt(offlineTimestamp, 10);
  }
}

/**
 * Lazily start the offline auth service. Intended for apps that explicitly
 * need offline connectivity checks; importing the module alone will no longer
 * start the interval.
 */
export function startOfflineAuthService(): OfflineAuthService {
  return OfflineAuthService.getInstance();
}

/**
 * Return the started instance if one exists, otherwise null. Useful for
 * callers that want to avoid side-effects in apps that do not need offline.
 */
export function getOfflineAuthService(): OfflineAuthService | null {
  return (OfflineAuthService as any).instance ?? null;
}

// Backward compatibility: provide a proxy that lazily creates the instance on
// first property access. This keeps existing imports working without eagerly
// starting the connectivity check at module load.
export const offlineAuthService: OfflineAuthService = new Proxy(
  {} as OfflineAuthService,
  {
    get(_target, prop, _receiver) {
      const instance = OfflineAuthService.getInstance();
      const value = (instance as any)[prop];
      return typeof value === 'function' ? value.bind(instance) : value;
    },
  }
);
